import React, { createContext, useContext, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, Eye, EyeOff, FileText, KeyRound, Send, Upload, X } from 'lucide-react';
import { partnerSessionsApi } from '../../services/partnerSessionsApi';
import type { CompanionSessionCard } from '../../services/companionApi';
import type { SessionAnswerValue, SessionFileAnswer, SessionQuestion } from '../../types/aai';
import { AppLogoWall } from './AppLogoWall';
import { asApps, asFiles, asStrings } from '../../pages/orbit-room/sessions/sessionShared';

/*
 * A session the partner sent this person, answered inside the AI Companion:
 * one question per card, as an open answer, a choice, a 1 to 5 scale, the
 * app logo wall for any question about tools, or uploaded documents.
 * Every question needs an answer before it is sent, and once sent the session
 * leaves the companion.
 */

export interface AnswerApi {
  upload: (sessionId: string, questionId: string, file: File) => Promise<{ data: { files: SessionFileAnswer[] } }>;
  remove: (sessionId: string, questionId: string, fileId: string) => Promise<{ data: { files: SessionFileAnswer[] } }>;
  submit: (sessionId: string, answers: Record<string, SessionAnswerValue>) => Promise<unknown>;
}

const MINE: AnswerApi = {
  upload: partnerSessionsApi.uploadMine,
  remove: partnerSessionsApi.removeMine,
  submit: partnerSessionsApi.submitMine,
};

const AnswerApiContext = createContext<AnswerApi>(MINE);

const errorText = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
};

const segmentTone = (n: number, step: number, filled: boolean): string => {
  if (n < step) return 'bg-ink';
  if (n === step && filled) return 'bg-accent';
  if (n === step) return 'bg-ink/25';
  return 'bg-canvas-inset';
};

const KEPT_KEY = ['queued', 'pending', 'set'];

/* A key accepted (or still being checked) from an earlier answer needs
   nothing more; only a rejected one is asked for again. */
const keyKept = (q: SessionQuestion): boolean => q.type === 'secret' && KEPT_KEY.includes(q.key?.status || '');

const isFilled = (q: SessionQuestion, value: SessionAnswerValue | undefined): boolean => {
  if (keyKept(q)) return true;
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (q.type === 'scale') return typeof value === 'number';
  return String(value).trim().length > 0;
};

const choiceTone = (on: boolean): string => {
  if (on) return 'border-ink bg-ink text-white';
  return 'border-line bg-white text-ink hover:border-ink';
};

const hint = (q: SessionQuestion): string => {
  if (q.type === 'single') return 'Pick one';
  if (q.type === 'multi') return 'Pick all that apply';
  if (q.type === 'tools') return 'Tap every app that applies';
  if (q.type === 'scale') return '1 to 5';
  if (q.type === 'file') return `Up to ${MAX_FILES} files, 25 MB each`;
  if (keyKept(q)) return 'Nothing to do here. Press Next.';
  if (q.type === 'secret') return 'Sent straight to the project\'s secure Vault. Nobody sees the full key.';
  return 'Ctrl or ⌘ + Enter to continue';
};

const MAX_FILES = 5;
const ACCEPT = '.pdf,.docx,.md,.txt,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.gif,.webp';

const fileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

/* Each file uploads as soon as it is picked, so the answer is what the server
   holds, and a removed file is gone from storage too. */
const FileAnswer: React.FC<{
  sessionId: string;
  question: SessionQuestion;
  value: SessionAnswerValue | undefined;
  onChange: (v: SessionAnswerValue) => void;
}> = ({ sessionId, question: q, value, onChange }) => {
  const files = asFiles(value);
  const api = useContext(AnswerApiContext);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const upload = async (list: FileList | null) => {
    const picked = Array.from(list || []).slice(0, MAX_FILES - files.length);
    if (!picked.length) return;
    setBusy(true);
    setError('');
    try {
      for (const file of picked) {
        const { data } = await api.upload(sessionId, q.id, file);
        onChange(data.files);
      }
    } catch (err) {
      setError(errorText(err, 'Could not upload that file. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (fileId: string) => {
    setBusy(true);
    setError('');
    try {
      const { data } = await api.remove(sessionId, q.id, fileId);
      onChange(data.files);
    } catch (err) {
      setError(errorText(err, 'Could not remove that file. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  let pickLabel = 'Choose files to upload';
  if (busy) pickLabel = 'Uploading…';
  let pickTone = 'cursor-pointer hover:border-ink';
  if (busy) pickTone = 'cursor-wait opacity-60';
  return (
    <div className="mt-3 space-y-2">
      {files.map((f) => (
        <div key={f.id} className="flex items-center gap-2.5 rounded-[12px] border border-line bg-white px-3.5 py-2.5">
          <FileText size={15} className="shrink-0 text-ink-muted" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-ink">{f.name}</span>
          <small className="shrink-0 text-[11.5px] tabular-nums text-ink-dim">{fileSize(f.size)}</small>
          <button type="button" onClick={() => remove(f.id)} disabled={busy} aria-label={`Remove ${f.name}`} className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-ink-dim hover:bg-canvas-inset hover:text-ink disabled:opacity-40">
            <X size={13} />
          </button>
        </div>
      ))}
      {files.length < MAX_FILES && (
        <label className={`flex items-center justify-center gap-2 rounded-[12px] border border-dashed border-line-strong px-3.5 py-4 text-[13px] font-semibold text-ink transition-colors ${pickTone}`}>
          <Upload size={14} aria-hidden />
          {pickLabel}
          <input
            type="file"
            multiple
            accept={ACCEPT}
            disabled={busy}
            className="sr-only"
            aria-label={q.label}
            onChange={(e) => {
              upload(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
      )}
      {error && <p className="m-0 text-[12px] text-danger">{error}</p>}
    </div>
  );
};

/* A key is typed hidden by default, never echoed anywhere else, and leaves
   this card only inside the submit. */
const KeyAnswer: React.FC<{
  question: SessionQuestion;
  value: SessionAnswerValue | undefined;
  onChange: (v: SessionAnswerValue) => void;
}> = ({ question: q, value, onChange }) => {
  const [shown, setShown] = useState(false);
  let text = '';
  if (typeof value === 'string') text = value;
  let type = 'password';
  if (shown) type = 'text';
  let toggleLabel = 'Show the key';
  if (shown) toggleLabel = 'Hide the key';
  const envChip = q.env_name && (
    <span className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-canvas-inset px-2.5 py-1 font-mono text-[11px] font-semibold text-ink-muted">
      <KeyRound size={11} aria-hidden /> {q.env_name}
    </span>
  );
  if (keyKept(q)) {
    return (
      <div className="mt-3">
        {envChip}
        <p className="m-0 flex items-center gap-1.5 rounded-[12px] border border-line bg-canvas px-3.5 py-3 text-[13px] text-ink">
          <Check size={14} aria-hidden /> You already gave this key. {q.key?.status_label}.
        </p>
      </div>
    );
  }
  return (
    <div className="mt-3">
      {envChip}
      {q.key?.status === 'rejected' && (
        <p className="m-0 mb-2 text-[12.5px] leading-[18px] text-danger">
          The key you gave last time was not accepted. {q.key.reason}
        </p>
      )}
      <div className="flex items-center gap-2 rounded-[12px] border border-line bg-white px-3.5 focus-within:border-ink focus-within:ring-[3px] focus-within:ring-ink/10">
        <input
          type={type}
          value={text}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste the key"
          aria-label={q.label}
          className="h-11 min-w-0 flex-1 border-0 bg-transparent font-mono text-[13px] text-ink outline-none placeholder:font-sans placeholder:text-ink-soft"
        />
        <button type="button" onClick={() => setShown(!shown)} aria-label={toggleLabel} title={toggleLabel} className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-ink-dim hover:bg-canvas-inset hover:text-ink">
          {shown && <EyeOff size={14} />}
          {!shown && <Eye size={14} />}
        </button>
      </div>
    </div>
  );
};

const AnswerInput: React.FC<{
  sessionId: string;
  question: SessionQuestion;
  value: SessionAnswerValue | undefined;
  onChange: (v: SessionAnswerValue) => void;
  onSubmit: () => void;
}> = ({ sessionId, question: q, value, onChange, onSubmit }) => {
  if (q.type === 'file') {
    return <FileAnswer sessionId={sessionId} question={q} value={value} onChange={onChange} />;
  }
  if (q.type === 'secret') {
    return <KeyAnswer question={q} value={value} onChange={onChange} />;
  }
  if (q.type === 'tools') {
    return <AppLogoWall value={asApps(value)} onChange={onChange} />;
  }
  if (q.type === 'single') {
    return (
      <div className="mt-3 grid gap-2" role="radiogroup" aria-label={q.label}>
        {(q.options || []).map((o) => (
          <button key={o} type="button" role="radio" aria-checked={value === o} onClick={() => onChange(o)} className={`flex items-center gap-2.5 rounded-[12px] border px-3.5 py-2.5 text-left text-[13.5px] font-medium transition-colors ${choiceTone(value === o)}`}>
            <span className="grid h-4 w-4 shrink-0 place-items-center rounded-full border border-current" aria-hidden>
              {value === o && <span className="h-2 w-2 rounded-full bg-current" />}
            </span>
            {o}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === 'multi') {
    const picked = asStrings(value);
    const toggle = (o: string) => {
      if (picked.includes(o)) onChange(picked.filter((x) => x !== o));
      else onChange([...picked, o]);
    };
    return (
      <div className="mt-3 grid gap-2" role="group" aria-label={q.label}>
        {(q.options || []).map((o) => {
          const on = picked.includes(o);
          return (
            <button key={o} type="button" role="checkbox" aria-checked={on} onClick={() => toggle(o)} className={`flex items-center gap-2.5 rounded-[12px] border px-3.5 py-2.5 text-left text-[13.5px] font-medium transition-colors ${choiceTone(on)}`}>
              <span className="grid h-4 w-4 shrink-0 place-items-center rounded-[4px] border border-current" aria-hidden>
                {on && <Check size={11} strokeWidth={3} />}
              </span>
              {o}
            </button>
          );
        })}
      </div>
    );
  }
  if (q.type === 'scale') {
    return (
      <div className="mt-3">
        <div className="grid grid-cols-5 gap-2" role="radiogroup" aria-label={q.label}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" role="radio" aria-checked={value === n} onClick={() => onChange(n)} className={`h-12 rounded-[12px] border text-[16px] font-bold tabular-nums transition-colors ${choiceTone(value === n)}`}>
              {n}
            </button>
          ))}
        </div>
        {(q.low || q.high) && (
          <div className="mt-1.5 flex justify-between gap-3 text-[11.5px] text-ink-muted">
            <span>{q.low}</span>
            <span className="text-right">{q.high}</span>
          </div>
        )}
      </div>
    );
  }
  let text = '';
  if (typeof value === 'string') text = value;
  return (
    <textarea
      key={q.id}
      autoFocus
      rows={6}
      value={text}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          onSubmit();
        }
      }}
      placeholder="Write your answer"
      aria-label={q.label}
      className="mt-2.5 block min-h-[150px] w-full resize-y rounded-[12px] border border-line bg-white px-3.5 py-3 text-[13.5px] leading-[20px] text-ink outline-none transition-shadow placeholder:text-ink-soft focus:border-ink focus:ring-[3px] focus:ring-ink/10"
    />
  );
};

export const SessionDeck: React.FC<{
  title: string;
  session: CompanionSessionCard;
  onBack: () => void;
  onSent: () => void;
  api?: AnswerApi;
  startLabel?: string;
}> = ({ title, session, onBack, onSent, api = MINE, startLabel = 'All tasks' }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, SessionAnswerValue>>(() => {
    const start: Record<string, SessionAnswerValue> = {};
    session.questions.forEach((q) => {
      if (q.answer !== undefined && q.answer !== null && q.answer !== '') start[q.id] = q.answer;
    });
    return start;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const total = session.questions.length;
  const question = session.questions[step];
  if (!question) return null;
  const value = answers[question.id];
  const last = step === total - 1;
  const filled = isFilled(question, value);

  const send = async () => {
    setBusy(true);
    setError('');
    try {
      await api.submit(session.id, answers);
      onSent();
    } catch (err) {
      setError(errorText(err, 'Could not send your answers. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (!filled || busy) return;
    if (last) {
      send();
      return;
    }
    setStep(step + 1);
  };

  const back = () => {
    if (step === 0) {
      onBack();
      return;
    }
    setStep(step - 1);
  };

  let nextLabel = 'Next';
  if (last) nextLabel = 'Send answers';
  if (busy) nextLabel = 'Sending…';
  let nextIcon = <ArrowRight size={14} aria-hidden />;
  if (last) nextIcon = <Send size={13} aria-hidden />;
  let backLabel = 'Back';
  if (step === 0) backLabel = startLabel;

  return (
    <AnswerApiContext.Provider value={api}>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-4 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <b className="block text-[16px] font-bold leading-[21px] text-ink">{title}</b>
            <small className="mt-0.5 block text-[12px] text-ink-muted">From {session.sender}</small>
          </div>
          <span className="shrink-0 rounded-full bg-canvas-inset px-2.5 py-1 font-mono text-[10.5px] font-semibold tabular-nums text-ink">
            {step + 1} / {total}
          </span>
        </div>

        <div className="mt-3 flex gap-1" aria-hidden>
          {session.questions.map((q, n) => (
            <span key={q.id} className={`h-1 flex-1 rounded-full transition-colors ${segmentTone(n, step, filled)}`} />
          ))}
        </div>

        {step === 0 && session.description && (
          <p className="m-0 mt-4 rounded-card bg-canvas-inset px-3.5 py-2.5 text-[12.5px] leading-[18px] text-ink-muted">
            {session.description}
          </p>
        )}

        <p className="m-0 mt-4 text-[15px] font-semibold leading-[21px] text-ink">{question.label}</p>
        <AnswerInput
          key={question.id}
          sessionId={session.id}
          question={question}
          value={value}
          onChange={(v) => setAnswers((current) => ({ ...current, [question.id]: v }))}
          onSubmit={next}
        />
        <small className="mt-1.5 block text-[11px] text-ink-soft">{hint(question)}</small>
        {error && <p className="m-0 mt-2 text-[12px] text-danger">{error}</p>}
      </div>

      <footer className="flex shrink-0 items-center justify-between gap-2 border-t border-line px-4 py-3">
        {step === 0 && !startLabel && <span />}
        {(step > 0 || startLabel) && <button
          type="button"
          onClick={back}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full border border-line-strong bg-white px-3.5 py-2 text-[12.5px] font-semibold text-ink hover:border-ink disabled:opacity-50 whitespace-nowrap"
        >
          <ArrowLeft size={14} aria-hidden />
          {backLabel}
        </button>}
        <button
          type="button"
          onClick={next}
          disabled={!filled || busy}
          className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-[12.5px] font-semibold text-white hover:bg-ink/90 disabled:cursor-not-allowed disabled:opacity-35 whitespace-nowrap"
        >
          {nextLabel}
          {nextIcon}
        </button>
      </footer>
    </AnswerApiContext.Provider>
  );
};

export default SessionDeck;
